import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import CardPlaceholder from "../components/CardPlaceholder";
import Pagination from "../components/Pagination";
import { searchCards } from "../lib/api";
import { cardPath } from "../lib/slug";

const PAGE_SIZE = 30;

/**
 * Global card search results.
 *
 * URL state (FIX 3B, owner directive 2026-02-01):
 *   /search?q=<query>&page=<n>
 *
 *   Both params are the single source of truth so back/refresh/share
 *   restore exactly what the user was looking at. The shared
 *   `Pagination` component drives page changes via URL rewrites.
 *
 * Multi-term matching (FIX 4) is a backend concern — this page just
 * forwards `q` and page as-is.
 */
export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const pageRaw = parseInt(params.get("page") || "1", 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const [data, setData] = useState(null);
  const [state, setState] = useState(q ? "loading" : "idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!q) {
      setState("idle");
      setData(null);
      return;
    }
    setState("loading");
    searchCards(q, { page, pageSize: PAGE_SIZE })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setState("ready");
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Search failed");
        setState("error");
      });
    return () => {
      alive = false;
    };
  }, [q, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 0;

  function handlePageChange(next) {
    // Keep q, replace page — using setParams preserves back-button
    // history so refresh restores this same page.
    const params = new URLSearchParams();
    params.set("q", q);
    params.set("page", String(next));
    setParams(params);
  }

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-8 md:px-gutter md:py-10"
      data-testid="search-results-page"
    >
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Search" },
        ]}
      />

      <div className="mt-6">
        <h1 className="text-title-page text-ink-text">
          {q ? <>Results for <span className="text-indigo-text">"{q}"</span></> : "Search"}
        </h1>
        {data && (
          <div
            className="mt-2 text-meta text-ink-muted tabular"
            data-testid="search-result-count"
          >
            {data.total} card{data.total === 1 ? "" : "s"}
            {totalPages > 1 && ` · page ${page} of ${totalPages}`}
          </div>
        )}
      </div>

      {state === "loading" && (
        <div className="mt-12 text-body text-ink-muted" data-testid="search-loading">
          Searching…
        </div>
      )}
      {state === "error" && (
        <div className="mt-12 text-body text-ink-muted" data-testid="search-error">
          {error}
        </div>
      )}
      {state === "ready" && data.results.length === 0 && (
        <div
          className="mt-12 rounded-card border border-ink-chip bg-ink-surface p-8 text-body text-ink-muted"
          data-testid="search-empty"
        >
          No cards match "{q}".
        </div>
      )}

      {state === "ready" && data.results.length > 0 && (
        <>
          <ul
            data-testid="search-results-list"
            className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2"
          >
            {data.results.map((r) => (
              <li key={r.id}>
                <Link
                  to={cardPath({
                    game: "pokemon",
                    lang: "en",
                    setName: r.set?.name || r.set?.external_id,
                    cardName: r.name,
                    number: r.number,
                    variantSlug: r.default_variant_slug,
                    defaultVariantSlug: r.default_variant_slug,
                  })}
                  data-testid={`search-result-${r.external_id}`}
                  className="animate-rise flex gap-4 rounded-card border border-ink-chip bg-ink-surface p-3 transition-colors duration-cc ease-cc-ease hover:border-ink-muted"
                >
                  <div className="w-[64px] shrink-0">
                    {r.reference_image_url ? (
                      <img
                        src={r.reference_image_url}
                        alt={r.name}
                        className="w-full rounded-chip object-cover"
                        style={{ aspectRatio: "5 / 7" }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="rounded-chip overflow-hidden">
                        <CardPlaceholder />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <div className="text-card-name text-ink-text">{r.name}</div>
                      <div className="mt-1 text-meta text-ink-muted tabular">
                        #{r.number} · {r.set?.name || "unknown set"}
                      </div>
                    </div>
                    <div className="text-meta tabular text-ink-muted">
                      {r.effective_price != null
                        ? `$${Number(r.effective_price).toFixed(2)}`
                        : "—"}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={handlePageChange}
            testId="search-pagination"
          />
        </>
      )}
    </div>
  );
}
